import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";

import { api, errMsg, errStatus } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { useToast } from "../../context/ToastContext.js";
import { Button } from "../../components/ui/button.js";
import { AsyncButton } from "../../components/ui/async-button.js";
import { Textarea } from "../../components/ui/textarea.js";
import { Badge } from "../../components/ui/badge.js";
import { Field, LockedNotice, ReviewKindBadge, SectionCard, fmtDate } from "./shared.js";
import SkillsSection from "./sections/SkillsSection.js";
import AssignedGoalsSection from "./sections/AssignedGoalsSection.js";
import TemporaryManagerSection from "./sections/TemporaryManagerSection.js";
import YearEndSection from "./sections/YearEndSection.js";
import ProbationSection from "./sections/ProbationSection.js";
import HrAttachments from "./sections/HrAttachments.js";
import type { ReviewDetail } from "./types.js";

export default function ReviewDetailPage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();

  const [review, setReview] = useState<ReviewDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get<ReviewDetail>(`/performance/reviews/${id}`);
      setReview(res.data);
      setError("");
    } catch (e) {
      logError("ReviewDetailPage.load", e);
      // A temporary manager whose access was revoked/expired since the page
      // loaded gets a 403 here — show that plainly rather than a bare "Forbidden".
      setError(
        errStatus(e) === 403
          ? "You no longer have access to this review."
          : (errMsg(e) ?? "Could not load this review"),
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <p className="p-8 text-surface-500">Loading…</p>;
  if (error || !review) {
    return (
      <div className="p-8">
        <p className="mb-4 text-[var(--color-error-fg)]">{error || "Review not found"}</p>
        <Button asChild variant="outline" size="sm">
          <Link to="/performance">
            <ArrowLeft size={14} /> Back to reviews
          </Link>
        </Button>
      </div>
    );
  }

  const { header, capabilities: caps } = review;
  // Probation is a status on the annual review; `probation` is null when the
  // employee has none (or the viewer may not see it).
  const probation = review.probation;

  return (
    <div className="flex w-full flex-col gap-5 p-8">
      <div>
        <Button asChild variant="ghost" size="sm" className="mb-3 -ml-2">
          <Link to="/performance">
            <ArrowLeft size={14} /> All reviews
          </Link>
        </Button>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h1 className="text-[22px] font-bold">{header.fullName}</h1>
              <ReviewKindBadge
                onProbation={review.onProbation}
                extended={!!probation?.isThisReview && probation.extensionCount > 0}
              />
              {review.hr.acknowledgementDate && (
                <Badge tone="success">
                  <Lock size={11} /> Finalized {fmtDate(review.hr.acknowledgementDate)}
                </Badge>
              )}
            </div>
            <p className="text-[13px] text-surface-500">
              {review.fiscalYearLabel} · {header.employeeCode} · {header.designation}
            </p>
          </div>
        </div>
      </div>

      {caps.hrLocked && (
        <p className="rounded-lg border border-[var(--color-info-border)] bg-[var(--color-info-bg-subtle)] px-4 py-3 text-[13px] text-[var(--color-info-fg)]">
          HR acknowledged this review on {fmtDate(review.hr.acknowledgementDate)}. It is now read-only.
        </p>
      )}

      {/* ── Employee header ── */}
      <SectionCard title="Employee information" collapsible>
        <dl className="grid gap-x-6 gap-y-4 text-[13px] sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6">
          <HeaderItem label="Employee ID" value={header.employeeCode} />
          <HeaderItem label="Full name" value={header.fullName} />
          <HeaderItem label="Department" value={header.department} />
          <HeaderItem label="Job title" value={header.designation} />
          <HeaderItem label="Role" value={header.role} />
          <HeaderItem label="Manager" value={header.manager} />
          <HeaderItem label="Location" value={header.location} />
          <HeaderItem label="Country" value={header.country} />
          <HeaderItem label="Experience with company" value={header.tenure} />
          <HeaderItem label="Joined" value={fmtDate(header.hireDate)} />
          {review.onProbation && <HeaderItem label="Probation ends" value={fmtDate(header.probationEndDate)} />}
          <HeaderItem label="Status" value={header.status} />
        </dl>
        {review.qcApplies && (
          <p className="mt-4 rounded-md bg-[var(--color-info-bg-subtle)] px-3 py-2 text-[12px] text-[var(--color-info-fg)]">
            This is a wireline division, so a QC-Score is required on every period review and counts
            towards the rating.
          </p>
        )}
      </SectionCard>

      {/* Probation sits right after the employee's details. The server never
          sends it to a temporary-only viewer, so no extra gate is needed here. */}
      {probation?.isThisReview && (
        <ProbationSection review={review} probation={probation} onChanged={() => void load()} />
      )}
      {probation && !probation.isThisReview && (
        <LockedNotice>
          This employee is still on probation, tracked on their{" "}
          <Link className="font-medium text-brand-600 hover:underline" to={`/performance/reviews/${probation.reviewId}`}>
            {probation.fiscalYearLabel} review
          </Link>
          .
        </LockedNotice>
      )}

      <SkillsSection review={review} onChanged={() => void load()} />
      <TemporaryManagerSection review={review} onChanged={() => void load()} />

      {/* A temporary manager who is not also the employee, line manager or HR
          sees only the employee header, skills (read-only) and the panel above.
          Goals, year-end and HR acknowledgement stay hidden for them (spec 3/10);
          an HR or line-manager delegate falls through and sees everything. */}
      {!caps.isTemporaryOnlyViewer && (
        <>
          <AssignedGoalsSection review={review} onChanged={() => void load()} />

          {/* The quarterly reviews card (PeriodAccordion) is hidden for now at the
              business's request. The component, its API and its data are all
              kept, so re-adding the card here brings it back. */}

          <YearEndSection review={review} onChanged={() => void load()} />

          {(caps.isHR || caps.isSuperuser) && (
            <HrAcknowledgementSection review={review} onChanged={() => void load()} />
          )}
        </>
      )}

    </div>
  );
}

function HeaderItem({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-[11px] font-medium tracking-wide text-surface-400 uppercase">{label}</dt>
      <dd className="font-medium">{value || <span className="font-normal text-surface-400">—</span>}</dd>
    </div>
  );
}

function HrAcknowledgementSection({
  review,
  onChanged,
}: {
  review: ReviewDetail;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [comments, setComments] = useState(review.hr.comments ?? "");
  // HR acknowledges last, after the manager's year-end.
  const ready = review.capabilities.canHrAcknowledge;

  async function save() {
    try {
      await api.put(`/performance/reviews/${review.id}/hr-acknowledgement`, { comments: comments || null });
      toast.show(
        review.hr.acknowledgementDate ? "HR comments saved" : "Review acknowledged and finalized",
        "success",
      );
      onChanged();
    } catch (e) {
      logError("ReviewDetailPage.hrAck", e);
      toast.show(errMsg(e) ?? "Could not save", "error");
      throw e;
    }
  }

  return (
    <SectionCard
      collapsible
      title="HR acknowledgement"
      description="Visible to HR only. The first save stamps the date and finalizes the review for everyone else."
      actions={
        review.hr.acknowledgementDate ? (
          <Badge tone="success">Acknowledged {fmtDate(review.hr.acknowledgementDate)}</Badge>
        ) : (
          <Badge tone="neutral">{ready ? "Pending" : "Waiting for manager"}</Badge>
        )
      }
    >
      {!ready && <LockedNotice>HR can acknowledge once the manager submits the year-end review.</LockedNotice>}
      <Field label="HR comments">
        <Textarea rows={3} value={comments} disabled={!ready} onChange={(e) => setComments(e.target.value)} />
      </Field>
      <div className="mt-3">
        <HrAttachments
          reviewId={review.id}
          attachments={review.hr.attachments}
          disabled={!ready}
          onChanged={onChanged}
        />
      </div>
      <div className="mt-3 flex justify-end">
        <AsyncButton size="sm" disabled={!ready} onClick={() => save()}>
          {review.hr.acknowledgementDate ? "Save comments" : "Acknowledge and finalize"}
        </AsyncButton>
      </div>
    </SectionCard>
  );
}
